<template>
  <!-- Actions -->
  <HeaderBar :modifiers="['transparent', 'form']">
    <div class="header-bar__title typo-title">
      {{ pageTitle }}
    </div>
    <template #right>
      <a
        class="button"
        @click="$router.back()"
      >
        {{ $t('general.cancel') }}
      </a>
      <FormSubmitButton
        :buttonLabel="buttonLabel"
        :disabled="saving"
        @click="buttonAction"
      />
    </template>
  </HeaderBar>
  <div class="user-permissions">
    <div class="user-permissions__section">
      <NoticeMessage
        v-if="!isLoading && userStatus"
        iconStyle="warning"
        :message="$t('user_operations.account_activation')"
        :modifiers="['resend']"
      >
        <u
          class="user-permissions__section--pointer user-permissions__section--margin-left"
          @click="resendInvitation"
        >
          {{ $t('user_operations.resend_invite') }}
        </u>
      </NoticeMessage>
      <div class="user-permissions__section-row">
        <template v-if="user">
          <div class="user-permissions__section-heading typo-small-title">
            {{ $t('profile.details_title') }}
          </div>
          <UserInformation
            v-if="user && !isLoading"
            :user="user"
          />
        </template>
      </div>
      <!-- Invite email form field -->
      <FormRow
        v-if="!user && !isLoading"
        :errors="errors && errors.user.email"
        :label="$t('activerecord.attributes.user.email')"
        labelFor="email"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input">
          <div
            :class="{
              input: true,
              'input--error': errors && errors.user.email,
            }"
          >
            <input
              id="email"
              v-model="userEmail"
              type="email"
              @input="validateEmail"
            >
          </div>
        </div>
      </FormRow>
    </div>
    <div
      v-if="showOrgAdminCheckbox"
      class="user-permissions__section"
    >
      <div class="user-permissions__section-admin">
        <Checkbox
          id="organisation_administrator"
          v-model="checked"
          :modifiers="['inline', 'small']"
          @change="handleCheckboxState()"
        >
          <p class="typo-small-title">
            {{ $t('user_operations.organisation_administrator_checkbox') }}
          </p>
        </Checkbox>
        <div class="typo-small user-permissions__section-admin-label">
          {{ $t('user_operations.organisation_administrator_checkbox_message') }}
        </div>
      </div>
    </div>
    <div class="user-permissions__section">
      <div class="user-permissions__section-heading typo-small-title">
        {{ $t('user_operations.operation_permissions') }}
      </div>
      <Loader v-if="isLoading" />
      <div
        v-for="(roleItem, name, index) in roleItems"
        :key="index"
        class="user-permissions__section-accordion"
      >
        <UserAccordion
          :active="fetchPanelIndex(index)"
          :genericHeader="true"
          :modifiers="['flat']"
        >
          <template #header-main>
            <div class="user-permissions__accordion-title">
              {{ $t(`nav.${name}`) }}
              <span class="typo-tiny">
                {{
                  pluralize(
                    totalOperations(roleItem),
                    $t('activerecord.models.operation.one'),
                    $t('activerecord.models.operation.other')
                  )
                }}
              </span>
            </div>
          </template>
          <template #content>
            <div
              v-for="(operation, value) in roleItem"
              :id="name"
              :key="operation"
            >
              <div class="user-permissions__division typo-input">
                {{ divisionLabel(value) }}
              </div>
              <UserRoleCard
                v-for="role in operation"
                :key="role.id"
                :currentRole="role"
                :operationType="name"
                :roleTypes="roleTypes[name]"
                @update="updateRoleData"
              />
            </div>
          </template>
        </UserAccordion>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { deleteStorage, fetchIndex } from '@/functions/local_storage.js';
import I18n from 'i18n-js';
import pluralize from '@/functions/pluralize';
import useAjax from '@/functions/use_ajax';
import useEmitter from '@/functions/use_emitter';

// Components
import Checkbox from '@/components/form/checkbox.vue';
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';
import UserAccordion from '@/components/users/user_accordion.vue';
import UserInformation from '@/components/users/user_information.vue';
import UserRoleCard from '@/components/users/user_role_card.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  userId: { type: String, required: false, default: null },
});

const router = useRouter();
const route = useRoute();
const ajax = useAjax();
const emitter = useEmitter();

const roleItems = ref({});
const roleTypes = ref({});
const userEmail = ref(null);
const user = ref(null);
const isLoading = ref(true);
const errors = ref(null);
const saving = ref(false);
const showOrgAdminCheckbox = ref(null);
const storageName = 'userManagementPanel';
const storageOperationName = 'userManagementOperation';

let checked = ref(null);

const roleData = ref({
  user: {
    email: userEmail,
    user_operations_attributes: [],
    user_shafts_attributes: [],
    user_shafts_v2_shafts_attributes: [],
    user_ships_attributes: [],
    user_equipments_attributes: [],
    user_organisations_attributes: [],
  },
});

const buttonLabel = computed(() => {
  return user.value
    ? I18n.t('general.save')
    : I18n.t('user_operations.send_invite');
});

const pageTitle = computed(() => {
  return user.value
    ? I18n.t('user_operations.user_permissions')
    : I18n.t('user_operations.new_user');
});

const buttonAction = computed(() => {
  return user.value ? updateUser : sendInvite;
});

const userStatus = computed(() => {
  return user.value && user.value?.status !== 'active';
});

onMounted(() => {
  props.userId ? loadUserOperations() : loadDefaultOperations();
});

onUnmounted(() => {
  const condition = ['operationUsers', 'editUser', 'allUsers', 'allUsersScoped', 'inviteUser', 'userProfile', 'userPassword'];
  deleteStorage(storageName, route.name, condition);
  deleteStorage(storageOperationName, route.name, condition);
});

async function loadUserOperations() {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/users/${props.userId}/edit`);

  if (ok) {
    roleItems.value = {
      plants: data.plants.user_operations,
      shafts: data.shafts.user_shafts,
      shafts_v2_shafts: data.shafts_v2_shafts.user_shafts_v2_shafts,
      ships: data.ships.user_ships,
      equipment: data.equipment.user_equipments,
    };

    showOrgAdminCheckbox.value = data.display_organisation_administrator;

    roleTypes.value = formatRoleData(data);
    user.value = data.user;
    checked.value = user.value.user_organisations ? user.value.user_organisations.length > 0 : false;

    isLoading.value = false;
  }
}

async function loadDefaultOperations() {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/users/new`);

  if (ok) {
    roleItems.value = {
      plants: data.plants.operations,
      shafts: data.shafts.operations,
      shafts_v2_shafts: data.shafts_v2_shafts.operations,
      ships: data.ships.operations,
      equipment: data.equipment.operations,
    };

    showOrgAdminCheckbox.value = data.display_organisation_administrator;

    roleTypes.value = formatRoleData(data);
    checked.value = false;

    isLoading.value = false;
  }
}

function formatRoleData(data) {
  return {
    plants: data.plants.roles,
    shafts: data.shafts.roles,
    shafts_v2_shafts: data.shafts_v2_shafts.roles,
    ships: data.ships.roles,
    equipment: data.equipment.roles,
  };
}

/**
 * Gets the total number of operations
 * used in accordion title
 */
function totalOperations(data) {
  return Object.values(data).reduce((count, value) => count + value.length, 0);
}

/**
 * Render generic text or Division name
 */
function divisionLabel(value) {
  switch (value) {
    case 'operations':
    case 'ships':
    case 'shafts':
    case 'shafts_v2':
    case 'shafts_v2_shafts':
    case 'equipment':
      return I18n.t('user_operations.default_operations');
    default:
      return value;
  }
}

/**
 * Updates current roleData
 *  or adds a new item to the array (user_operations_attributes: [])
 */
function updateRoleData(currentRoleState, operationType) {
  let userOperationAttributes = roleData.value.user.user_operations_attributes;
  let userShaftsAttributes = roleData.value.user.user_shafts_attributes;
  let userShaftsV2Attributes = roleData.value.user.user_shafts_v2_shafts_attributes;
  let userShipsAttributes = roleData.value.user.user_ships_attributes;
  let userEquipmentAttributes = roleData.value.user.user_equipments_attributes;

  if (operationType === 'plants') {
    if (!userOperationAttributes.some((userOp) => userOp.operation_id === currentRoleState.operation_id)) {
      userOperationAttributes.push(currentRoleState);
    } else {
      userOperationAttributes = userOperationAttributes.map((userOp) => {
        if (userOp.operation_id === currentRoleState.operation_id) {
          return Object.assign(userOp, currentRoleState);
        }
      });
    }
  }

  if (operationType === 'shafts') {
    if (!userShaftsAttributes.some((userOp) => userOp.shaft_id === currentRoleState.shaft_id)) {
      userShaftsAttributes.push(currentRoleState);
    } else {
      userShaftsAttributes = userShaftsAttributes.map((userOp) => {
        if (userOp.shaft_id === currentRoleState.shaft_id) {
          return Object.assign(userOp, currentRoleState);
        }
      });
    }
  }

  if (operationType === 'shafts_v2_shafts') {
    if (!userShaftsV2Attributes.some((userOp) => userOp.shafts_v2_shaft_id === currentRoleState.shafts_v2_shaft_id)) {
      userShaftsV2Attributes.push(currentRoleState);
    } else {
      userShaftsV2Attributes = userShaftsV2Attributes.map((userOp) => {
        if (userOp.shafts_v2_shaft_id === currentRoleState.shafts_v2_shaft_id) {
          return Object.assign(userOp, currentRoleState);
        }
      });
    }
  }

  if (operationType === 'ships') {
    if (!userShipsAttributes.some((userOp) => userOp.ship_id === currentRoleState.ship_id)) {
      userShipsAttributes.push(currentRoleState);
    } else {
      userShipsAttributes = userShipsAttributes.map((userOp) => {
        if (userOp.ship_id === currentRoleState.ship_id) {
          return Object.assign(userOp, currentRoleState);
        }
      });
    }
  }

  if (operationType === 'equipment') {
    if (!userEquipmentAttributes.some((userOp) => userOp.equipment_id === currentRoleState.equipment_id)) {
      userEquipmentAttributes.push(currentRoleState);
    } else {
      userEquipmentAttributes = userEquipmentAttributes.map((userOp) => {
        if (userOp.equipment_id === currentRoleState.equipment_id) {
          return Object.assign(userOp, currentRoleState);
        }
      });
    }
  }
}

/**
 * Set up for sending data
 */
async function postUserData(url, method, message) {
  saving.value = true;

  let { ok, data } = await ajax(url, {
    method: method,
    body: { ...roleData.value },
  });

  if (ok) {
    errors.value = null;
    emitter.emit('toast', {
      status: 'success',
      message: I18n.t(`user_operations.user_${message}_toast`),
    });
    const paramsType = mapIndexToName(fetchIndex(storageName));
    router.push({ name: 'allUsersScoped', params: { type: paramsType } });
  } else {
    errors.value = data;
  }

  saving.value = false;
}

/**
 * Send invite
 */
function sendInvite() {
  return postUserData(`/organisations/${props.organisationId}/users`, 'POST', 'invite');
}

/**
 * Re-send invitation
 */
function resendInvitation() {
  return postUserData(`/organisations/${props.organisationId}/users/${user.value.id}/resend_invitation`, 'POST', 'invite');
}

/**
 * Update user
 */
function updateUser() {
  return postUserData(`/organisations/${props.organisationId}/users/${user.value.id}`, 'PATCH', 'update');
}

function validateEmail(e) {
  if (e.target.value) {
    errors.value = null;
  }
}

/**
 * Handles checkbox state when updating a user
 */
function orgAdminChekboxUpdate(checkedState) {
  if (checkedState) {
    roleData.value.user.user_organisations_attributes[0] = {
      id: null,
      organisation_id: props.organisationId,
    };
  }

  if (!checkedState) {
    if (user.value.user_organisations[0] === undefined) {
      roleData.value.user.user_organisations_attributes = [];
    } else {
      roleData.value.user.user_organisations_attributes[0] = {
        id: user.value.user_organisations[0].id,
        _destroy: '1',
      };
    }
  }
}

/**
 * Handles checkbox state when inviting a user
 */
const orgAdminChekboxNew = (checkedState) => {
  if (checkedState) {
    roleData.value.user.user_organisations_attributes[0] = {
      id: null,
      organisation_id: props.organisationId,
    };
  } else {
    roleData.value.user.user_organisations_attributes = [];
  }
};

function handleCheckboxState() {
  if (user.value) {
    return orgAdminChekboxUpdate(checked.value);
  } else {
    return orgAdminChekboxNew(checked.value);
  }
}

/**
 * Get the active panel index
 */
function fetchPanelIndex(index) {
  return !isNaN(fetchIndex(storageName, index));
}

/**
 * Returns the name for a particular index
 */
function mapIndexToName(name) {
  switch (name) {
    case 1:
      return 'shafts';
    case 2:
      return 'shafts_v2';
    case 3:
      return 'ships';
    case 4:
      return 'equipment';
    default:
      return 'plants';
  }
}
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

$layout-user-start-width: rem(250);

.user-permissions {
  @include breakpoint(medium) {
    padding: bu(2);
  }

  &__section {
    background-color: map-get($background-color, light);
    border-bottom: 2px solid map-get($background-color, dark);
    margin: 0 auto;
    max-width: $layout-form-large-max-width;
    padding: bu(3) bu(2);

    &--pointer {
      cursor: pointer;
    }

    &--margin-left {
      margin-left: bu(.5);
    }

    @include breakpoint(medium) {
      padding: bu(3) bu(4);
    }
  }

  &__section-admin {
    background: map-get($background-color, default);
    border-radius: map-get($border-radius, mega);
    padding: bu(2);
  }

  &__section-admin-label {
    margin-left: bu(4);
    margin-top: bu(.5);
    color: map-get($foreground-color, default);
    opacity: 0.69;
  }

  &__section-accordion {
    border-top: 1px solid map-get($background-color, dark);
    padding: bu(2.5) 0;
  }

  &__division {
    margin: bu(3) 0;
  }

  &__section-row {
    display: flex;
    flex-direction: column;

    @include breakpoint(medium) {
      flex-direction: row;
    }
  }

  &__section-heading {
    color: map-get($foreground-color, light);
    font-weight: 500;
    margin-bottom: bu(3);

    @include breakpoint(medium) {
      max-width: $layout-user-start-width;
      width: 100%;
    }
  }

  &__accordion-title {
    color: map-get($foreground-color, light);
    font-weight: 500;

    span {
      font-weight: normal;
      color: map-get($foreground-color, default);
      margin-left: bu(1);
    }
  }
}
</style>
